/** Factory tab — web mirror of `Sources/Views/FactoryView.swift`.
 * Health banner, a "Needs You" approvals section (Approve/Deny → resolve_approval
 * command), the tasks list (expandable detail), and the PR pipeline. All content
 * flows from the store. */

import { useState } from 'react';
import { useAppState, useEnv } from '../app/context';
import { Section, StatusPill, relativeShort, statusColor, statusIcon, statusLabel } from '../components/UIHelpers';
import { jsonDisplayString, type FactoryApprovalRow, type FactoryHealthRow, type FactoryPrTaskRow, type FactoryTaskRow } from '../models/factory';

export function FactoryView() {
  const env = useEnv();
  const state = useAppState();
  const { snapshot, isLoading } = state;

  async function resolve(approval: FactoryApprovalRow, decision: string) {
    await env.submit({
      tenant_id: approval.tenant_id,
      kind: 'resolve_approval',
      payload: { external_id: approval.external_id, decision },
    });
  }

  return (
    <>
      {snapshot.health ? (
        <Section>
          <div className="row">
            <HealthRow health={snapshot.health} />
          </div>
        </Section>
      ) : null}

      {snapshot.approvals.length > 0 ? (
        <Section header="Needs You" headerIcon="✋" warn>
          {snapshot.approvals.map((a) => (
            <ApprovalRow key={a.id} approval={a} onDecision={(d) => resolve(a, d)} />
          ))}
        </Section>
      ) : null}

      <Section header="Tasks">
        {snapshot.tasks.length === 0 && !isLoading ? (
          <div className="row muted">No tasks</div>
        ) : null}
        {isLoading && snapshot.tasks.length === 0 ? <div className="spinner" /> : null}
        {snapshot.tasks.map((t) => (
          <TaskRow key={t.id} task={t} />
        ))}
      </Section>

      <Section header="Pipeline · PRs">
        {snapshot.prTasks.length === 0 ? <div className="row muted">No open PRs</div> : null}
        {snapshot.prTasks.map((pr) => (
          <PRRow key={pr.id} pr={pr} />
        ))}
      </Section>
    </>
  );
}

function HealthRow({ health }: { health: FactoryHealthRow }) {
  return (
    <div className="health">
      <span className="health__dot" style={{ color: health.daemon_alive ? 'var(--green)' : 'var(--red)' }}>
        {health.daemon_alive ? '⚡' : '⭘'}
      </span>
      <div>
        <div className="row__title">{health.daemon_alive ? 'Daemon alive' : 'Daemon down'}</div>
        {health.last_heartbeat ? (
          <div className="caption">
            Heartbeat {relativeShort(health.last_heartbeat)} · {health.watchdog_state ?? '—'}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ApprovalRow({
  approval,
  onDecision,
}: {
  approval: FactoryApprovalRow;
  onDecision: (decision: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  async function decide(d: string) {
    setBusy(true);
    await onDecision(d);
  }
  return (
    <div className="row">
      <div className="row__head">
        <span className="row__title">🔧 {approval.tool ?? 'action'}</span>
        <span className="row__spacer" />
        {approval.task_ref ? <span className="mono muted">{approval.task_ref}</span> : null}
      </div>
      <div className="row__subtitle">{jsonDisplayString(approval.args)}</div>
      <div className="btn-row">
        <button className="btn btn--primary" disabled={busy} onClick={() => decide('approve')}>
          ✓ Approve
        </button>
        <button className="btn btn--secondary" disabled={busy} onClick={() => decide('deny')}>
          ✕ Deny
        </button>
      </div>
    </div>
  );
}

function TaskRow({ task }: { task: FactoryTaskRow }) {
  const [open, setOpen] = useState(false);
  const subtitle = task.current_action ?? task.result ?? task.error ?? statusLabel(task.status);
  return (
    <>
      <button className="row row--tappable" onClick={() => setOpen((v) => !v)}>
        <span style={{ color: statusColor(task.status) }}>{statusIcon(task.status)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row__head">
            <span className="row__title">{task.external_id}</span>
            {task.department ? <StatusPill text={task.department} color="var(--indigo)" /> : null}
            <span className="row__spacer" />
            {task.priority != null ? <span className="caption">P{task.priority}</span> : null}
          </div>
          <div className="row__subtitle">{subtitle}</div>
        </div>
        <span className="muted" aria-hidden>
          {open ? '⌄' : '›'}
        </span>
      </button>
      {open ? <TaskDetail task={task} /> : null}
    </>
  );
}

function TaskDetail({ task }: { task: FactoryTaskRow }) {
  const rows: Array<[string, string | null | undefined]> = [
    ['State', statusLabel(task.status)],
    ['Action', task.current_action],
    ['Result', task.result],
    ['Error', task.error],
    ['Repo', task.worktree_repo],
    ['Branch', task.branch],
    ['Source', task.source],
    ['Budget', task.budget_usd != null ? `$${task.budget_usd.toFixed(2)}` : null],
    ['Runtime', task.runtime_sec != null ? `${Math.round(task.runtime_sec)}s` : null],
  ];
  return (
    <div className="row" style={{ background: 'var(--bg-inset)' }}>
      {rows
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => (
          <div key={k} className="row__head" style={{ padding: '3px 0' }}>
            <span className="caption">{k}</span>
            <span className="row__spacer" />
            <span style={{ fontSize: 13, textAlign: 'right' }}>{v}</span>
          </div>
        ))}
    </div>
  );
}

function PRRow({ pr }: { pr: FactoryPrTaskRow }) {
  return (
    <div className="row">
      <div className="row__head">
        <span aria-hidden>⑃</span>
        <span className="row__title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pr.pr_key}
        </span>
        <span className="row__spacer" />
        {pr.sensitive ? <StatusPill text="sensitive" color="var(--red)" icon="🔒" /> : null}
      </div>
      <div className="row__head" style={{ gap: 6, marginTop: 6 }}>
        {pr.status ? <StatusPill text={pr.status} color="var(--blue)" /> : null}
        {pr.verdict ? <StatusPill text={pr.verdict} color="var(--purple)" /> : null}
        {pr.preview_url ? (
          <a href={pr.preview_url} target="_blank" rel="noreferrer" className="caption" style={{ color: 'var(--accent)' }}>
            preview ↗
          </a>
        ) : null}
      </div>
    </div>
  );
}
